import React from 'react';
import { Table, Image, Tag, Button, Space, Tooltip, TableProps } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';


export interface TableColumn {
  key: string;
  title: string;
  dataIndex?: string;
  width?: number;
  align?: 'left' | 'center' | 'right';
  ellipsis?: boolean | { showTitle: boolean };
  render?: (value: any, record: any, index: number) => React.ReactNode;
  sorter?: boolean | ((a: any, b: any) => number);
  filters?: Array<{ text: string; value: any }>;
  onFilter?: (value: any, record: any) => boolean;
}

export interface TableAction {
  key: string;
  label: string;
  icon?: React.ReactNode;
  type?: 'primary' | 'default' | 'dashed' | 'link' | 'text';
  danger?: boolean;
  disabled?: (record: any) => boolean;
  onClick: (record: any) => void;
  tooltip?: (record: any) => string;
}

export interface DataTableProps<T = any> {
  data: T[];
  columns: TableColumn[];
  actions?: TableAction[];
  loading?: boolean;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (current: number, size: number) => void;
  rowKey: string | ((record: T) => string);
  showSizeChanger?: boolean;
  showQuickJumper?: boolean;
  showTotal?: (total: number, range: [number, number]) => string;
  scroll?: { x?: number | string; y?: number | string };
  className?: string;
  size?: 'small' | 'middle' | 'large';
  bordered?: boolean;
  paginationPosition?: (
    | 'topLeft'
    | 'topCenter'
    | 'topRight'
    | 'bottomLeft'
    | 'bottomCenter'
    | 'bottomRight'
  )[];
  rowClassName?: TableProps<T>['rowClassName'];
  sticky?: boolean
}

const DataTable = <T extends Record<string, any>>({
  data,
  columns,
  actions = [],
  loading = false,
  totalItems,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  rowKey,
  showSizeChanger = false,
  showQuickJumper = true,
  showTotal,
  scroll = { x: 600 },
  className = 'data-table',
  size = 'middle',
  bordered = false,
  paginationPosition = ['bottomLeft'],
  rowClassName,
  sticky
}: DataTableProps<T>) => {
  // Add actions column if actions are provided
  const allColumns = [...columns];

  const { t } = useTranslation('common');

  if (actions.length > 0) {
    allColumns.push({
      key: 'actions',
      title: t('actions'),
      width: 150,
      align: 'center' as const,
      render: (_, record: T) => (
        <Space size="small">
          {actions.map((action) => (
            <Button
              key={action.key}
              type={action.type || 'primary'}
              size="small"
              icon={action.icon}
              danger={action.danger}
              disabled={action.disabled ? action.disabled(record) : false}
              onClick={() => action.onClick(record)}
              title={action.tooltip ? action.tooltip(record) : undefined}
            >
              {action.label}
            </Button>
          ))}
        </Space>
      ),
    });
  }

  return (
    <Table<T>
      columns={allColumns}
      dataSource={data}
      loading={loading}
      rowKey={rowKey}
      size={size}
      bordered={bordered}
      scroll={scroll}
      className={className}
      pagination={{
        current: currentPage,
        pageSize: pageSize,
        total: totalItems,
        onChange: onPageChange,
        onShowSizeChange: onPageSizeChange,
        showSizeChanger,
        showQuickJumper,
        showTotal,
        position: paginationPosition,
        locale: {
          jump_to: t('goTo'),
          page: t('page'),
          items_per_page: `/ ${t('page')}`,
        },
      }}
      rowClassName={rowClassName}
      sticky={sticky}
    />
  );
};

export default DataTable;
